import { useStore, useStoreApi } from "@xyflow/react";
import { useEffect } from "react";

import { ZOOM_SETTLE_MS } from "../hooks/useQuantizedZoom";

/** Scale ratio that forces a write mid-gesture; smaller steps wait to settle. */
const SCALE_STEP = 1.05;

const handleScale = (zoom: number) => Math.max(1 / zoom, 1);

/**
 * Keeps the connect handles (CanvasConnectHandles) a usable on-screen size
 * when zoomed out, the rule xyflow's `NodeResizeControl` applies to the
 * corner grips: `scale = max(1 / zoom, 1)`.
 *
 * Renders nothing; writes `--canvas-handle-scale` on the React Flow root for
 * canvas-styles.css, so the handles on every node need no zoom subscription.
 * The property inherits into every node, so a write restyles the whole
 * canvas: mid-gesture it is written only once the scale has moved 5%, and
 * exactly once the zoom settles — the cadence of useQuantizedZoom.
 */
export function CanvasHandleScale() {
  const store = useStoreApi();
  const domNode = useStore((s) => s.domNode);

  useEffect(() => {
    if (!domNode) return;
    let shown = 1;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const write = (scale: number) => {
      shown = scale;
      domNode.style.setProperty("--canvas-handle-scale", String(scale));
    };
    const onZoom = (zoom: number) => {
      const scale = handleScale(zoom);
      clearTimeout(settle);
      if (scale === shown) return;
      if (Math.max(scale / shown, shown / scale) >= SCALE_STEP) write(scale);
      else settle = setTimeout(() => write(scale), ZOOM_SETTLE_MS);
    };
    const unsubscribe = store.subscribe((state, prev) => {
      if (state.transform[2] !== prev.transform[2]) onZoom(state.transform[2]);
    });
    write(handleScale(store.getState().transform[2]));
    return () => {
      clearTimeout(settle);
      unsubscribe();
      domNode.style.removeProperty("--canvas-handle-scale");
    };
  }, [store, domNode]);

  return null;
}
